import {
	ChangeDetectionStrategy,
	Component,
	ContentChildren,
	QueryList,
	AfterViewInit,
	ChangeDetectorRef,
	Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabComponent } from './tab/tab.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router } from '@angular/router';
import { TestHookDirective } from '@core/ui';
import { TestHooks } from './tabs.hooks';

@UntilDestroy()
@Component({
	selector: 'ui-tabs',
	imports: [CommonModule, TestHookDirective],
	templateUrl: './tabs.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabsComponent implements AfterViewInit {
	testHooks = TestHooks;
	@ContentChildren(TabComponent) tabs!: QueryList<TabComponent>;
	@Input() queryParamKey = 'tab';
	@Input() storageKey = 'selectedTab'; // Allow customizing the storage key
	activeTab?: TabComponent;

	constructor(
		private cdr: ChangeDetectorRef,
		private router: Router,
		private route: ActivatedRoute,
	) {}

	ngAfterViewInit() {
		setTimeout(() => {
			// Subscribe to query params changes
			this.route.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
				const tabId = params[this.queryParamKey];
				if (tabId && this.tabs) {
					const tab = this.tabs.find((t) => t.id === tabId);
					if (tab) {
						this.selectTab(tab, false);
						return;
					}
				}

				// If no tab in URL, try localStorage
				const storedTabId = localStorage.getItem(this.storageKey);
				if (storedTabId && this.tabs) {
					const tab = this.tabs.find((t) => t.id === storedTabId);
					if (tab) {
						this.selectTab(tab);
						return;
					}
				}

				// If nothing found, select first tab
				this.selectFirstTab();
			});

			// Handle dynamic tabs
			this.tabs.changes.pipe(untilDestroyed(this)).subscribe(() => {
				if (!this.activeTab && this.tabs.length > 0) {
					this.selectFirstTab();
				}
			});
		});
	}

	private selectFirstTab(): void {
		if (this.tabs.first) {
			this.selectTab(this.tabs.first);
		}
	}

	selectTab(tab: TabComponent, updateUrl = true): void {
		if (this.activeTab !== tab) {
			this.activeTab = tab;

			// Save to localStorage
			localStorage.setItem(this.storageKey, tab.id);

			if (updateUrl) {
				this.router.navigate([], {
					relativeTo: this.route,
					queryParams: { [this.queryParamKey]: tab.id },
					queryParamsHandling: 'merge',
				});
			}
			this.cdr.markForCheck();
		}
	}

	isActive(tab: TabComponent): boolean {
		return this.activeTab === tab;
	}
}
